import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { DoctorAPI, type DoctorListItem, type DoctorFull } from "../../services/doctor.service";
import Sidebar from "../../components/Sidebar";
import MedicationCard from "../../components/prescription/MedicationCard";
import AddMedicineModal from "../../components/prescription/AddMedicineModal";
import InvestigationsTags from "../../components/prescription/InvestigationsTags";
import { DEV_DOCTOR_ID, DEV_PATIENT_ID, IS_DEV } from "../../constants";
import {
  DiagnosisAPI,
  PatientInfo,
  PrescriptionDocument,
  PrescriptionPdfResponse,
  PrescriptionResponse,
  ReviewMedication,
  VisitResponse,
} from "../../services/diagnosis.service";
import { AllergyAPI, AllergyApiError, type AllergyResponse } from "../../services/allergy.service";
import { reportAllergyCheckUnavailable } from "../../utils/flow-telemetry";
import { OnboardingAPI, ClinicData } from "../../services/onboarding.service";
import { SettingsAPI } from "../../services/settings.service";
import { SoapAPI, UserInfo } from "../../services/soap.service";
import { db } from "../../db/schema";
import { unwrapFromStorage } from "../../db/encryption-bridge";
import { enqueue } from "../../services/offline-sync.service";
import { useHrfSettings } from "../../hooks/useHrfSettings";
import type { PatientRecord, VisitRecord, PrescriptionRecord, AllergyRecord } from "../../db/db.types";
import { actionErrorMessage, isBackendUnreachable, isPermissionDenied } from "../../utils/network";
import { AccessDenied } from "../../components/RequirePermission";
import { getPatientByDisplayId } from "../../services/patient.service";
import { generateOfflinePrescriptionPdf } from "../../services/offline-pdf.service";
import { DateInput } from "../../components/ui/DateInput";
import { PrescriptionDeliveryAPI } from "../../services/prescription-delivery.service";
import { ApiError } from "../../services/api.errors";
import { isInsufficientCreditsError } from "../../services/credits.service";
import { useWalletBalance } from "../../hooks/useWalletBalance";
import { BuyCreditsModal } from "../../components/credits/BuyCreditsModal";
import { DentalSummary } from "../../components/dental/DentalSummary";



// ─── Allergy Severity Styles ───────────────────────────────────────────────
const ALLERGY_SEVERITY_BADGE: Record<string, string> = {
  "Life-Threatening": "text-red-700 bg-red-100 border-red-300",
  "Severe":           "text-orange-700 bg-orange-100 border-orange-200",
  "Moderate":         "text-amber-700 bg-amber-50 border-amber-200",
  "Mild":             "text-yellow-700 bg-yellow-50 border-yellow-200",
};

// ─── Mock prescription_document ───────────────────────────────────────────────
// Replace with actual AI Scribe API response when S7-02 is implemented.
// Set to null to test the manual flow (no AI pre-fill).

// ─── Icons ────────────────────────────────────────────────────────────────────
const IcArrowLeft = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M19 12H5" /><path d="m12 19-7-7 7-7" />
  </svg>
);
const IcDownload = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const IcClinic = () => (
  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <rect x="9" y="13" width="6" height="8" />
    <path d="M9 9h.01M12 9h.01M15 9h.01" />
  </svg>
);
const IcDocSection = () => (
  <svg className="w-5 h-5 text-sky-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);
const IcPillSection = () => (
  <svg className="w-5 h-5 text-sky-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
    <path d="m8.5 8.5 7 7" />
  </svg>
);
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _IcSend = () => (
  <svg className="w-5 h-5 text-white flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M22 2 11 13" /><path d="M22 2 15 22 11 13 2 9l20-7z" />
  </svg>
);
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _IcMail = () => (
  <svg className="w-5 h-5 text-slate-700 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);
const IcCheckCircle = () => (
  <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
// Speech-bubble icon in WhatsApp green so the WhatsApp action reads as a
// messaging action at a glance, not a phone call.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _IcWhatsapp = () => (
  <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);
const IcCalendarSmall = () => (
  <svg className="w-5 h-5 text-slate-700 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);
const IcPlus = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const IcWarning = () => (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const IcShield = () => (
  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
function calcAge(dob: string): number {
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function findMatchingAllergy(medName: string, allAllergies: AllergyResponse[]): AllergyResponse | null {
  const normMed = medName.trim().toLowerCase();
  if (!normMed) return null;
  for (const a of allAllergies) {
    const normAllergen = a.allergen.trim().toLowerCase();
    if (!normAllergen) continue;
    if (
      normMed === normAllergen ||
      normMed.includes(normAllergen) ||
      (normAllergen.length >= 4 && normAllergen.includes(normMed))
    ) {
      return a;
    }
  }
  return null;
}

function safeRandomUUID(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  if (typeof window !== "undefined" && window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return Math.random().toString(36).substring(2, 11);
}

function medsFromDoc(doc: PrescriptionDocument, patientAllergies: AllergyResponse[] = []): ReviewMedication[] {
  return doc.medications.map(m => {
    const match = findMatchingAllergy(m.name, patientAllergies);
    return {
      medication_id: safeRandomUUID(),
      name: m.name.toUpperCase(),
      dosage: m.unit ? `${m.strength} ${m.unit}` : m.strength,
      frequency: m.frequency,
      timing: m.timing,
      duration: m.duration ? `${m.duration} days` : undefined,
      quantity: m.quantity,
      instructions: m.instructions || undefined,
      ai_suggested: true,
      allergy_conflict: Boolean(m.allergy_conflict || match),
      conflicting_allergen: match?.allergen,
      override_reason: "",
    };
  });
}

function medsFromSaved(meds: PrescriptionResponse["medications"], patientAllergies: AllergyResponse[] = []): ReviewMedication[] {
  return meds.map(m => {
    const hasOverride = Boolean(m.override_reason?.trim());
    const match = findMatchingAllergy(m.name, patientAllergies);
    return {
      ...m,
      override_reason: m.override_reason ?? "",
      ai_suggested: false,
      allergy_conflict: Boolean(hasOverride || match),
      conflicting_allergen: match?.allergen,
    };
  });
}

const BLUE_GRADIENT = "bg-gradient-to-br from-blue-500 to-sky-400";

/** Format a YYYY-MM-DD date string → "15 June 2026" for PDF/prescription display. */
function fmtFollowUpDate(d: string): string {
  try {
    const dt = new Date(d + "T00:00:00");
    return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  } catch { return d; }
}

// ─── ContinueDiagnosis ────────────────────────────────────────────────────────
// Adapter — converts the AI Scribe service shape (drug_name, duration_days, etc.)
// into the PrescriptionDocument shape the review screen + MedicationCard consume.
// Kept here (not in the service file) so changes to the review shape are local.
type AiScribeDocLike = {
  diagnosis_label?: unknown;
  medications?: Array<{
    drug_name?: string;
    dosage?: string;
    frequency?: string;
    timing?: string | null;
    duration_days?: number;
    total_quantity?: number;
    instructions?: string | null;
    allergy_conflict?: boolean;
  }>;
  investigations?: unknown[];
  advice?: unknown;
  follow_up?: unknown;
};

// Gemini sometimes returns string fields as arrays or nested objects.
// Flatten anything to a plain string so the backend always receives a string.
function _coerceStr(v: unknown): string {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.map(_coerceStr).filter(Boolean).join(". ");
  if (typeof v === "object") return Object.values(v as object).map(_coerceStr).filter(Boolean).join("; ");
  return String(v);
}

function aiScribeDocToPrescriptionDoc(raw: AiScribeDocLike): PrescriptionDocument {
  return {
    diagnosis_label: _coerceStr(raw.diagnosis_label),
    medications: (raw.medications ?? []).map((m) => ({
      name: m.drug_name ?? (m as any).name ?? "",
      strength: m.dosage ?? (m as any).strength ?? "",
      unit: (m as any).unit ?? "",
      frequency: m.frequency ?? "",
      timing: m.timing ?? undefined,
      // PrescriptionDocument requires duration/quantity as numbers; medsFromDoc
      // treats 0 as falsy and renders as undefined, so 0 is a safe sentinel for
      // "Gemini didn't return this field" without breaking the type contract.
      duration: typeof m.duration_days === "number" ? m.duration_days : 0,
      quantity: typeof m.total_quantity === "number" ? m.total_quantity : 0,
      instructions: m.instructions ?? "",
      allergy_conflict: Boolean(m.allergy_conflict),
    })),
    investigations: (raw.investigations ?? []).map(_coerceStr).filter(Boolean),
    advice: _coerceStr(raw.advice),
    follow_up: _coerceStr(raw.follow_up),
  };
}

const ContinueDiagnosis: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Prefer real AI Scribe doc passed via router state; fall back to mock for
  // design preview / manual navigation. Guard against the key existing with an
  // undefined value (e.g. when the SOAP pad forwards a scribe result that had
  // no prescription_document) — otherwise the adapter dereferences undefined.
  const rawScribeDoc =
    location.state && typeof location.state === "object" && "prescription_document" in location.state
      ? (location.state as { prescription_document?: AiScribeDocLike }).prescription_document
      : undefined;
  const scribeDoc: PrescriptionDocument | null = rawScribeDoc
    ? aiScribeDocToPrescriptionDoc(rawScribeDoc)
    : null;
  const initialDoc: PrescriptionDocument | null = scribeDoc ?? null;
  const visitId      = searchParams.get("visit_id")   ?? "";
  const rawPatientId = searchParams.get("patient_id") || (IS_DEV ? DEV_PATIENT_ID : "");
  const doctorId     = searchParams.get("doctor_id")  || (IS_DEV ? DEV_DOCTOR_ID  : "");
  const isPatientDisplayId = rawPatientId.toUpperCase().startsWith("PAT-") && !rawPatientId.includes("?");
  const [patientId, setPatientId] = useState(isPatientDisplayId ? "" : rawPatientId);

  const visitIdRef = useRef(visitId);
  visitIdRef.current = visitId;

  const [patient, setPatient] = useState<PatientInfo | null>(null);
  const [clinic,  setClinic]  = useState<ClinicData | null>(null);
  const [clinicLogoUrl, setClinicLogoUrl] = useState<string | null>(null);
  const [doctor, setDoctor] = useState<UserInfo | null>(null);
  const [doctorRecord, setDoctorRecord] = useState<DoctorFull | DoctorListItem | null>(null);
  const [doctorDisplayName, setDoctorDisplayName] = useState("");
  const [visit, setVisit] = useState<VisitResponse | null>(null);
  const [prescriptions, setPrescriptions] = useState<PrescriptionResponse[]>([]);
  const [observation, setObservation] = useState("");
  const [obsStatus, setObsStatus] = useState<"idle" | "saving" | "saved">("idle");
  const obsIsDirtyRef = useRef(false);
  const [loading, setLoading] = useState(true);
  // Server refused a load (403) — holds its reason; null means "not refused".
  const [accessDenied, setAccessDenied] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [emailLoading,setEmailLoading]=useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [waLoading,setWaLoading]=useState(false);
  const [pdfResult, setPdfResult] = useState<PrescriptionPdfResponse | null>(null);
  const [pendingDelivery, setPendingDelivery] = useState<"email" | "whatsapp" | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { gateState: creditsGateState, refetch: refetchWallet } = useWalletBalance();
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Review state ─────────────────────────────────────────────────────────────
  const [reviewMeds, setReviewMeds] = useState<ReviewMedication[]>([]);
  const [allergies, setAllergies] = useState<AllergyResponse[]>([]);
  const allergiesRef = useRef<AllergyResponse[]>([]);
  useEffect(() => {
    allergiesRef.current = allergies;
  }, [allergies]);
  const lifeThreateningAllergies = useMemo(
    () => allergies.filter(a => a.is_life_threatening || a.severity === "Life-Threatening"),
    [allergies]
  );
  // Active life-threatening allergy conflicts with prescribed medications
  const conflictingLifeThreateningAllergies = useMemo(() => {
    const matched = new Set<string>();
    const result: AllergyResponse[] = [];
    for (const med of reviewMeds) {
      for (const allergy of lifeThreateningAllergies) {
        if (
          (med.conflicting_allergen && med.conflicting_allergen.toLowerCase() === allergy.allergen.toLowerCase()) ||
          findMatchingAllergy(med.name, [allergy])
        ) {
          if (!matched.has(allergy.allergy_id)) {
            matched.add(allergy.allergy_id);
            result.push(allergy);
          }
        }
      }
    }
    return result;
  }, [reviewMeds, lifeThreateningAllergies]);
  const conflictingLifeThreateningCount = conflictingLifeThreateningAllergies.length;
  const [diagnosis, setDiagnosis] = useState("");
  const [investigations, setInvestigations] = useState<string[]>([]);
  const [advice, setAdvice] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [addMedOpen, setAddMedOpen] = useState(false);
  const [editingMed, setEditingMed] = useState<ReviewMedication | null>(null);
  const [scribeError, setScribeError] = useState<boolean>(
    searchParams.get("scribe_error") === "503"
  );
  const [diagnosisAiSuggested, setDiagnosisAiSuggested] = useState(false);

  // ── Voice input ──────────────────────────────────────────────────────────────
  const [voiceField, setVoiceField] = useState<string | null>(null); // which field is recording
  const voiceRecRef = useRef<any>(null);

  const toggleVoice = (field: string, getValue: () => string, setValue: (v: string) => void) => {
    if (voiceField === field) {
      // Stop
      if (voiceRecRef.current) { voiceRecRef.current.onend = null; voiceRecRef.current.stop(); voiceRecRef.current = null; }
      setVoiceField(null);
      return;
    }
    // Stop any other active recording
    if (voiceRecRef.current) { voiceRecRef.current.onend = null; voiceRecRef.current.stop(); voiceRecRef.current = null; }

    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { alert("Speech recognition not supported. Use Chrome or Edge."); return; }

    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-IN";
    const base = getValue();
    let finalT = base ? base + " " : "";

    rec.onresult = (e: any) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalT += e.results[i][0].transcript + " ";
        else interim = e.results[i][0].transcript;
      }
      setValue((finalT + interim).trim());
    };
    rec.onerror = (e: any) => {
      setVoiceField(null); voiceRecRef.current = null;
      if (e.error === "not-allowed") alert("Microphone access denied.");
    };
    rec.onend = () => { setVoiceField(null); voiceRecRef.current = null; };

    voiceRecRef.current = rec;
    setVoiceField(field);
    try { rec.start(); } catch { setVoiceField(null); voiceRecRef.current = null; }
  };

  const VoiceBtn: React.FC<{ field: string; getValue: () => string; setValue: (v: string) => void }> = ({ field, getValue, setValue }) => (
    <button
      type="button"
      onClick={() => toggleVoice(field, getValue, setValue)}
      className={`flex items-center gap-1 text-xs font-medium transition-colors ${
        voiceField === field ? "text-red-500 animate-pulse" : "text-cyan-600 hover:text-cyan-700"
      }`}
    >
      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
        <path d="M19 10v2a7 7 0 01-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
      </svg>
      {voiceField === field ? "Stop" : "Voice"}
    </button>
  );

  // Clinic-configured Health Record Format rules (Settings → HRF & MPR).
  const hrf = useHrfSettings();

  // Finalize is allowed only when every card is Confirmed and every conflict card has an override reason
  const hasAiSuggested = reviewMeds.some(m => m.ai_suggested);
  const hasUnresolvedConflicts = reviewMeds.some(
    m => (m.allergy_conflict || Boolean(findMatchingAllergy(m.name, allergies))) && !m.override_reason.trim()
  );
  const canFinalize = !hasAiSuggested && !hasUnresolvedConflicts;

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, type });
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const handler = (e: Event) => {
      const { tempId, serverId } = (e as CustomEvent<{ tempId: string; serverId: string }>).detail;
      if (visitIdRef.current === tempId) {
        setSearchParams(p => { p.set("visit_id", serverId); return p; }, { replace: true });
      }
    };
    window.addEventListener("kaagazy:visit-remapped", handler);
    return () => window.removeEventListener("kaagazy:visit-remapped", handler);
  }, [setSearchParams]);

  useEffect(() => {
    if (!isPatientDisplayId) return;
    getPatientByDisplayId(rawPatientId)
      .then(p => setPatientId(p.patient_id ?? ""))
      .catch(() => showToast("Patient not found", "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPatientDisplayId, rawPatientId]);

  // Load patient allergies with offline IndexedDB fallback
  useEffect(() => {
    if (!patientId) {
      allergiesRef.current = [];
      setAllergies([]);
      return;
    }
    let cancelled = false;
    const loadAllergies = async () => {
      try {
        const data = await AllergyAPI.list(patientId);
        if (!cancelled) {
          allergiesRef.current = data;
          setAllergies(data);
        }
      } catch (e) {
        if (e instanceof AllergyApiError && e.statusCode === 404) {
          if (!cancelled) {
            allergiesRef.current = [];
            setAllergies([]);
          }
        } else {
          try {
            const rawRows = await db.allergies.where("patient_id").equals(patientId).toArray();
            if (rawRows.length > 0) {
              const decrypted = await Promise.all(
                rawRows.map(r => unwrapFromStorage<AllergyRecord>(r as unknown as Record<string, unknown>))
              );
              if (!cancelled) {
                const mappedAllergies: AllergyResponse[] = decrypted.map(r => ({
                  allergy_id:          r.allergy_id,
                  patient_id:          r.patient_id,
                  allergen:            r.allergen,
                  severity:            r.severity,
                  reaction_type:       r.reaction_type ?? null,
                  reaction:            r.reaction ?? null,
                  verified_date:       r.verified_date ?? null,
                  notes:               r.notes ?? null,
                  created_at:          r.created_at ?? new Date().toISOString(),
                  is_life_threatening: r.severity === "Life-Threatening",
                }));
                allergiesRef.current = mappedAllergies;
                setAllergies(mappedAllergies);
              }
            } else {
              // Offline, an empty cache is the offline-first path working.
              // Online, the server failed and the review checks against nothing.
              if (navigator.onLine) {
                reportAllergyCheckUnavailable("server list failed, no local copy", e);
              }
              if (!cancelled) {
                allergiesRef.current = [];
                setAllergies([]);
              }
            }
          } catch (cacheErr) {
            reportAllergyCheckUnavailable("local allergy cache unreadable", cacheErr);
            if (!cancelled) {
              allergiesRef.current = [];
              setAllergies([]);
            }
          }
        }
      }
    };
    loadAllergies();
    return () => { cancelled = true; };
  }, [patientId]);

  // Reconcile allergy conflicts whenever allergies change
  useEffect(() => {
    if (allergies.length === 0) return;
    setReviewMeds(prev =>
      prev.map(m => {
        const match = findMatchingAllergy(m.name, allergies);
        if (match) {
          return {
            ...m,
            allergy_conflict: true,
            conflicting_allergen: m.conflicting_allergen || match.allergen,
          };
        } else if (!m.override_reason?.trim()) {
          return {
            ...m,
            allergy_conflict: false,
            conflicting_allergen: undefined,
          };
        }
        return m;
      })
    );
  }, [allergies]);

  // Track current step for resume routing
  useEffect(() => {
    if (!visitId) return;
    DiagnosisAPI.updateVisitStep?.(visitId, "continue-diagnosis")?.catch?.(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitId]);

  // Auto-save observation (debounce 1.5s)
  useEffect(() => {
    if (!obsIsDirtyRef.current) return;
    const rxId = prescriptions[0]?.prescription_id;
    if (!visitId || !rxId) return;
    const timer = setTimeout(async () => {
      setObsStatus("saving");
      try {
        await DiagnosisAPI.patchObservation(visitId, rxId, observation);
        setObsStatus("saved");
        setTimeout(() => setObsStatus("idle"), 2000);
      } catch (e) {
        // A refused write must never be queued to replay as if we were offline.
        if (!isPermissionDenied(e) && isBackendUnreachable(e)) {
          enqueue("update", "prescription", rxId, {
            visit_id: visitId, prescription_id: rxId, observation,
          } as Record<string, unknown>).catch(() => {});
        }
        setObsStatus("idle");
      }
    }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [observation]);

  useEffect(() => {
    if (!visitId || !patientId || !doctorId) {
      if (!isPatientDisplayId || patientId) setLoading(false);
      return;
    }

    const loadPatient = () =>
      DiagnosisAPI.getPatient(patientId)
        .then(setPatient)
        .catch(async (err) => {
          // This fallback takes ANY error, so a 403 would otherwise render the
          // cached patient the grant was just revoked for.
          if (isPermissionDenied(err)) {
            setAccessDenied(actionErrorMessage(err, "You don't have access to this patient."));
            return;
          }
          const raw = await db.patients.get(patientId).catch(() => undefined);
          if (raw) {
            const p = await unwrapFromStorage<PatientRecord>(raw as unknown as Record<string, unknown>);
            setPatient({
              patient_id: p.patient_id,
              first_name: p.first_name,
              last_name: p.last_name,
              date_of_birth: p.date_of_birth,
              gender: p.gender,
              contact_number: p.contact_number,
            });
          }
        });

    loadPatient();

    Promise.all([
      (SoapAPI.getUser?.(doctorId) ?? Promise.resolve(null)).catch(() => null),
      DiagnosisAPI.getVisit(visitId),
      DiagnosisAPI.getPrescriptions(visitId),
      (SoapAPI.getNote?.(visitId) ?? Promise.resolve(null)).catch(() => null),
      (DoctorAPI.get?.(doctorId) ?? Promise.resolve(null)).catch(() => null),
      (OnboardingAPI.getClinic?.() ?? Promise.resolve(null)).catch(() => null),
    ]).then(async ([d, v, rx, soapNote, docRecord, clinicData]) => {
      // Resolve doctor record from doctors table (prefer docRecord, fallback to v.visit.doctor_id)
      let resolvedDoc: DoctorFull | DoctorListItem | null = docRecord;
      const visitDoctorId = v?.visit?.doctor_id;
      if (!resolvedDoc && visitDoctorId) {
        resolvedDoc = await (DoctorAPI.get?.(visitDoctorId) ?? Promise.resolve(null)).catch(() => null);
      }
      setDoctorRecord(resolvedDoc);

      // Display name for the page — prefer doctors table name
      let displayName = d ? `Dr. ${d.first_name} ${d.last_name}` : "";
      if (resolvedDoc?.name) {
        displayName = /^dr\.?\s/i.test(resolvedDoc.name) ? resolvedDoc.name : `Dr. ${resolvedDoc.name}`;
      }
      setDoctor(d);
      setDoctorDisplayName(displayName);
      if (clinicData) {
        setClinic(clinicData);
        // Fetch clinic logo URL from settings API (has presigned logo_url)
        SettingsAPI.getClinicSettings().then(s => {
          if (s.logo_url) setClinicLogoUrl(s.logo_url);
        }).catch(() => {});
      }

      // Merge SOAP note fields into visit if visit fields are empty
      const visitData = v?.visit;
      if (visitData?.patient_id && !patientId) {
        setPatientId(visitData.patient_id);
      }
      if (soapNote && visitData) {
        if (!visitData.chief_complaint && soapNote.chief_complaint) visitData.chief_complaint = soapNote.chief_complaint;
        if (!visitData.assessment && soapNote.primary_dx) visitData.assessment = soapNote.primary_dx;
        if (!visitData.advice && soapNote.patient_education) visitData.advice = soapNote.patient_education;
      }
      setVisit(visitData);
      setPrescriptions(rx);
      if (rx[0]?.observation) setObservation(rx[0].observation);

      // ── Initialize review state ──────────────────────────────────────────────
      const doc = initialDoc;
      const currentAllergies = allergiesRef.current.length > 0 ? allergiesRef.current : allergies;
      if (doc) {
        setDiagnosis(doc.diagnosis_label);
        setDiagnosisAiSuggested(Boolean(doc.diagnosis_label));
        setInvestigations(doc.investigations);
        setAdvice(doc.advice);
        // doc.follow_up is formatted text — prefer visit.follow_up_date (YYYY-MM-DD)
        setFollowUp(v?.visit?.follow_up_date || "");
        setReviewMeds(medsFromDoc(doc, currentAllergies));
      } else if (rx[0]) {
        // Prescription exists — use it as primary
        // If SOAP note exists, it overwrites (AI Scribe takes priority over manual 90s input)
        if (soapNote) {
          setDiagnosis(soapNote.primary_dx || soapNote.clinical_impression || rx[0].notes || v?.visit?.notes || "");
          setInvestigations(
            soapNote.procedures
              ? soapNote.procedures.split(/[,;\n]/).map((t: string) => t.trim()).filter(Boolean)
              : v?.visit?.lab_tests?.length ? v.visit.lab_tests : []
          );
          setAdvice(soapNote.patient_education || v?.visit?.advice || "");
          // follow_up from SOAP is free text — prefer visit.follow_up_date (YYYY-MM-DD) for the date picker
          setFollowUp(v?.visit?.follow_up_date || "");
          // Build comprehensive observation from all SOAP fields
          const obs = [
            soapNote.hpi && `HPI: ${soapNote.hpi}`,
            soapNote.ros && `ROS: ${soapNote.ros}`,
            soapNote.physical_exam && `Physical Exam: ${soapNote.physical_exam}`,
            soapNote.lab_results && `Lab Results: ${soapNote.lab_results}`,
            soapNote.clinical_impression && `Clinical Impression: ${soapNote.clinical_impression}`,
            soapNote.differential_dx && `Differential Dx: ${soapNote.differential_dx}`,
            soapNote.referrals && `Referrals: ${soapNote.referrals}`,
          ].filter(Boolean).join("\n\n");
          if (obs) setObservation(obs);
        } else {
          setDiagnosis(rx[0].notes || v?.visit?.notes || "");
          setInvestigations(v?.visit?.lab_tests?.length ? v.visit.lab_tests : []);
          setAdvice(v?.visit?.advice || "");
          // rx[0].follow_up is formatted text — prefer visit.follow_up_date (YYYY-MM-DD)
          setFollowUp(v?.visit?.follow_up_date || "");
          if (rx[0].observation) setObservation(rx[0].observation);
        }
        setReviewMeds(medsFromSaved(rx[0].medications, currentAllergies));
      } else if (soapNote) {
        // No prescription — SOAP note overwrites everything from 90s diagnosis
        setDiagnosis(soapNote.primary_dx || soapNote.clinical_impression || v.visit.notes || "");
        setInvestigations(
          soapNote.procedures
            ? soapNote.procedures.split(/[,;\n]/).map((t: string) => t.trim()).filter(Boolean)
            : v.visit.lab_tests ?? []
        );
        setAdvice(soapNote.patient_education || v.visit.advice || "");
        // follow_up from SOAP is free text — prefer visit.follow_up_date (YYYY-MM-DD) for the date picker
        setFollowUp(v.visit.follow_up_date || "");
        // Build comprehensive observation from all SOAP fields
        const obsParts = [
          soapNote.hpi && `HPI: ${soapNote.hpi}`,
          soapNote.ros && `ROS: ${soapNote.ros}`,
          soapNote.physical_exam && `Physical Exam: ${soapNote.physical_exam}`,
          soapNote.lab_results && `Lab Results: ${soapNote.lab_results}`,
          soapNote.medications_text && `Medications: ${soapNote.medications_text}`,
          soapNote.clinical_impression && `Clinical Impression: ${soapNote.clinical_impression}`,
          soapNote.differential_dx && `Differential Dx: ${soapNote.differential_dx}`,
          soapNote.referrals && `Referrals: ${soapNote.referrals}`,
        ].filter(Boolean);
        if (obsParts.length) setObservation(obsParts.join("\n\n"));
      } else {
        // No prescription, no SOAP note — use 90s diagnosis visit data
        setDiagnosis(v.visit.notes ?? v.visit.assessment ?? "");
        setInvestigations(v.visit.lab_tests ?? []);
        setAdvice(v.visit.advice ?? "");
        setFollowUp(v.visit.follow_up_date ?? "");
      }
      // ────────────────────────────────────────────────────────────────────────
    }).catch(async (err) => {
      // Same catch-all shape as loadPatient: without this a 403 on the visit or
      // its prescriptions repopulated both from IndexedDB below.
      if (isPermissionDenied(err)) {
        setAccessDenied(actionErrorMessage(err, "You don't have access to this visit."));
        return;
      }
      console.warn("Failed to load visit context", err);
      // Apply the initial doc (AI Scribe response if present, else mock) so the
      // figma flow is testable even when the backend visit/prescription endpoints fail.
      const mockDoc = initialDoc;
      if (mockDoc) {
        setDiagnosis(mockDoc.diagnosis_label);
        setDiagnosisAiSuggested(Boolean(mockDoc.diagnosis_label));
        setInvestigations(mockDoc.investigations);
        setAdvice(mockDoc.advice);
        setFollowUp("");  // mock path — no date to prefill
        const currentAllergies = allergiesRef.current.length > 0 ? allergiesRef.current : allergies;
        setReviewMeds(medsFromDoc(mockDoc, currentAllergies));
      }
      try {
        const rawVisit = await db.visits.get(visitId);
        const rawRxRows = await db.prescriptions.where("visit_id").equals(visitId).toArray();

        if (rawVisit) {
          const v = await unwrapFromStorage<VisitRecord>(rawVisit as unknown as Record<string, unknown>);
          const visitData: VisitResponse = {
            visit_id:        v.visit_id,
            patient_id:      v.patient_id,
            doctor_id:       v.doctor_id,
            visit_date:      v.visit_date,
            status:          v.status,
            chief_complaint: v.chief_complaint ?? undefined,
            notes:           v.notes ?? undefined,
            lab_tests:       v.lab_tests ?? undefined,
            advice:          v.advice ?? undefined,
            follow_up_date:  v.follow_up_date ?? undefined,
            dental_chart:    v.dental_chart ?? null,
          };
          setVisit(visitData);
          setInvestigations(visitData.lab_tests ?? []);
          setAdvice(visitData.advice ?? "");
        }

        if (rawRxRows.length > 0) {
          const rxList = await Promise.all(
            rawRxRows.map(r => unwrapFromStorage<PrescriptionRecord>(r as unknown as Record<string, unknown>))
          );
          const mapped = rxList.map(r => ({
            prescription_id: r.prescription_id,
            visit_id:        r.visit_id,
            prescribed_by:   r.prescribed_by,
            prescribed_at:   r.prescribed_at ?? new Date().toISOString(),
            status:          r.status,
            notes:           r.notes ?? undefined,
            observation:     r.observation ?? undefined,
            medications:     (r.medications ?? []).map(m => ({
              medication_id: m.medication_id,
              drug_id:       m.drug_id ?? undefined,
              name:          m.name,
              dosage:        m.dosage,
              frequency:     m.frequency,
              route:         m.route ?? undefined,
              duration:      m.duration ?? undefined,
              quantity:      m.quantity ?? undefined,
              instructions:  m.instructions ?? undefined,
              timing:        (m as any).timing ?? undefined,
              override_reason: (m as any).override_reason ?? undefined,
            })),
          }));
          setPrescriptions(mapped);
          const firstObs = rxList[0]?.observation;
          if (firstObs) setObservation(firstObs);

          // Offline review state — same initialDoc fallback as the online path.
          const doc = initialDoc;
          const currentAllergies = allergiesRef.current.length > 0 ? allergiesRef.current : allergies;
          if (doc) {
            setDiagnosis(doc.diagnosis_label);
            setInvestigations(doc.investigations);
            setAdvice(doc.advice);
            setFollowUp("");  // offline path — no date to prefill
            setReviewMeds(medsFromDoc(doc, currentAllergies));
          } else if (mapped[0]) {
            setDiagnosis(mapped[0].notes ?? "");
            setFollowUp("");
            setReviewMeds(medsFromSaved(mapped[0].medications, currentAllergies));
          }
        }

        let cachedUser = await db.users.get(doctorId).catch(() => undefined);
        if (!cachedUser) {
          const all = await db.users.toArray().catch(() => []);
          cachedUser = all[0];
        }
        const cachedClinic = cachedUser?.clinic_id
          ? await db.clinics.get(cachedUser.clinic_id).catch(() => undefined)
          : undefined;
        if (cachedUser) {
          setDoctor(cachedUser);
          setDoctorDisplayName(`Dr. ${cachedUser.first_name} ${cachedUser.last_name}`);
        }
        if (cachedClinic) setClinic(cachedClinic as ClinicData);
      } catch (dexieErr) {
        console.error("IndexedDB fallback failed:", dexieErr);
      }
    }).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitId, patientId, doctorId, isPatientDisplayId]);

  const latestRx = prescriptions[0] ?? null;

  const errMessage = (e: unknown, fallback: string): string =>
    e instanceof Error ? e.message : fallback;

  const isTrustedPdfUrl = (url: string): boolean => {
    try {
      const u = new URL(url);
      if (u.protocol !== "https:") return false;
      const host = u.hostname.toLowerCase();
      return host.endsWith(".amazonaws.com") || host.endsWith(".s3.amazonaws.com");
    } catch {
      return false;
    }
  };

  // Invalidate PDF cache when review state changes
  const medsKey = JSON.stringify(reviewMeds);
  useEffect(() => {
    if (pdfResult) setPdfResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medsKey, observation]);

  // ── Medication handlers ───────────────────────────────────────────────────────

  const handleConfirmMed = (id: string) => {
    setReviewMeds(prev => prev.map(m => m.medication_id === id ? { ...m, ai_suggested: false } : m));
  };

  const handleEditMed = (id: string) => {
    const med = reviewMeds.find(m => m.medication_id === id);
    if (med) { setEditingMed(med); setAddMedOpen(true); }
  };

  const handleRemoveMed = (id: string) => {
    setReviewMeds(prev => prev.filter(m => m.medication_id !== id));
  };

  const handleOverrideChange = (id: string, reason: string) => {
    setReviewMeds(prev => prev.map(m => m.medication_id === id ? { ...m, override_reason: reason } : m));
  };

  const handleSaveMed = (saved: Omit<ReviewMedication, "ai_suggested" | "allergy_conflict" | "override_reason">) => {
    const currentAllergies = allergiesRef.current.length > 0 ? allergiesRef.current : allergies;
    const match = findMatchingAllergy(saved.name, currentAllergies);
    setReviewMeds(prev => {
      const idx = prev.findIndex(m => m.medication_id === saved.medication_id);
      if (idx >= 0) {
        // Edit — confirm at the same time
        const updated = [...prev];
        updated[idx] = {
          ...prev[idx],
          ...saved,
          ai_suggested: false,
          allergy_conflict: Boolean(match),
          conflicting_allergen: match ? match.allergen : undefined,
        };
        return updated;
      }
      // New card added manually — check for allergy conflicts
      return [
        ...prev,
        {
          ...saved,
          ai_suggested: false,
          allergy_conflict: Boolean(match),
          conflicting_allergen: match ? match.allergen : undefined,
          override_reason: "",
        },
      ];
    });
    setEditingMed(null);
    setAddMedOpen(false);
  };

  const handleOpenAddMed = () => {
    setEditingMed(null);
    setAddMedOpen(true);
  };

  // ── Submit ────────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!visitId) return;

    // HRF rule: a diagnosis must be documented before the visit can be finalized.
    if (hrf.mandatory_diagnosis && !diagnosis.trim()) {
      showToast("Diagnosis is required before finalizing this visit", "error");
      return;
    }

    setSubmitting(true);
    try {
      const confirmedMeds = reviewMeds.map(m => ({
        drug_id:         m.drug_id,
        name:            m.name,
        dosage:          m.dosage,
        frequency:       m.frequency,
        timing:          m.timing,
        route:           m.route,
        duration:        m.duration,
        quantity:        m.quantity,
        instructions:    m.instructions,
        override_reason: m.allergy_conflict ? m.override_reason : undefined,
      }));

      const prescriptionPayload = {
        notes: diagnosis || undefined,
        medications: confirmedMeds,
        diagnosis_label: diagnosis || undefined,
        investigations: investigations.length > 0 ? investigations : undefined,
        advice: advice || undefined,
        follow_up: followUp ? fmtFollowUpDate(followUp) : undefined,
        ai_scribe_source: scribeDoc !== null,
      };

      // Save the prescription, then FINALISE it (Draft → Active + PDF). "Finalize"
      // must produce a finalised prescription so the send page can email/WhatsApp it.
      // An empty prescription is allowed — the backend keeps it Draft (no longer
      // auto-discontinued) and finalises it just the same.
      let rxId: string | null = latestRx?.prescription_id ?? null;
      if (latestRx) {
        await DiagnosisAPI.updatePrescription(visitId, latestRx.prescription_id, prescriptionPayload);
      } else {
        const created = await DiagnosisAPI.createPrescription(visitId, prescriptionPayload);
        rxId = created.prescription_id;
      }
      if (rxId) {
        await PrescriptionDeliveryAPI.finalise(rxId);
      }

      await DiagnosisAPI.finalizeVisit(visitId);
      navigate(`/diagnosis-complete?visit_id=${visitId}&patient_id=${rawPatientId}&doctor_id=${doctorId}`, { replace: true });
    } catch (e) {
      if (isPermissionDenied(e)) {
        showToast(actionErrorMessage(e, "Failed to finalize visit"), "error");
      } else if (isBackendUnreachable(e)) {
        try {
          const localVisit = await db.visits.get(visitId);
          const op = localVisit?.sync_status === "synced" ? "update" : "create";
          await enqueue(
            op,
            "visit",
            visitId,
            op === "create"
              ? { ...localVisit, status: "Finalized" } as Record<string, unknown>
              : { status: "Finalized" } as Record<string, unknown>
          );
          navigate(`/diagnosis-complete?visit_id=${visitId}&patient_id=${rawPatientId}&doctor_id=${doctorId}`, { replace: true });
          return;
        } catch {
          showToast("Failed to queue finalize", "error");
        }
      } else {
        showToast(errMessage(e, "Failed to finalize visit"), "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── PDF handlers ──────────────────────────────────────────────────────────────

  // Build the current prescription payload from the form state
  const _buildPrescriptionPayload = () => {
    const confirmedMeds = reviewMeds.map(m => ({
      drug_id: m.drug_id, name: m.name, dosage: m.dosage,
      frequency: m.frequency, timing: m.timing, route: m.route,
      duration: m.duration, quantity: m.quantity, instructions: m.instructions,
    }));
    return {
      notes: diagnosis || undefined,
      medications: confirmedMeds,
      diagnosis_label: diagnosis || undefined,
      investigations: investigations.length > 0 ? investigations : undefined,
      advice: advice || undefined,
      follow_up: followUp ? fmtFollowUpDate(followUp) : undefined,
      observation: observation || undefined,
    };
  };

  // Save the current form state to DB before PDF generation so the PDF has all data
  const _saveCurrentState = async (): Promise<string> => {
    if (!visitId) throw new Error("No visit ID");

    // Save visit-level fields (chief complaint, advice, investigations)
    await DiagnosisAPI.patchVisit(visitId, {
      chief_complaint: visit?.chief_complaint || undefined,
      advice: advice || undefined,
      lab_tests: investigations.length > 0 ? investigations : undefined,
    }).catch(() => {}); // best-effort

    const payload = _buildPrescriptionPayload();

    if (latestRx) {
      await DiagnosisAPI.updatePrescription(visitId, latestRx.prescription_id, payload);
      return latestRx.prescription_id;
    } else {
      const created = await DiagnosisAPI.createPrescription(visitId, payload);
      const rxList = await DiagnosisAPI.getPrescriptions(visitId);
      setPrescriptions(rxList);
      return rxList[0]?.prescription_id || created.prescription_id;
    }
  };

  const _getPdfResult = async (pharmacyOnly = false): Promise<PrescriptionPdfResponse> => {
    // Always save current form state before generating PDF
    const rxId = await _saveCurrentState();
    if (!visitId || !rxId) throw new Error("No prescription to generate PDF for");
    // Clear cached PDF so it regenerates with latest data
    const result = await DiagnosisAPI.generatePdf(visitId, rxId, pharmacyOnly);
    setPdfResult(result);
    return result;
  };

  // Render the prescription PDF locally from IndexedDB data. Used when the
  // backend is unreachable so doctors can still download a prescription
  // mid-consult; otherwise mirrors the server PDF's structure.
  const downloadOfflineCopy = (): boolean => {
    if (!latestRx || !patient || !visit) return false;
    try {
      generateOfflinePrescriptionPdf({
        patient,
        visit,
        prescription: latestRx,
        doctor: doctor ? {
          name: doctorRecord?.name || `${doctor.first_name || ""} ${doctor.last_name || ""}`.trim(),
          first_name: doctor.first_name,
          last_name: doctor.last_name,
          specialization: (doctorRecord?.specializations && doctorRecord.specializations.length > 0)
            ? doctorRecord.specializations.join(", ")
            : (doctorRecord?.specialization || doctor.specialization),
          specializations: doctorRecord?.specializations,
          registration_number: doctorRecord?.registration_number || doctor.registration_number,
          qualifications: doctorRecord?.qualifications || undefined,
          department: doctorRecord?.department || undefined,
          display_id: doctorRecord?.display_id || undefined,
        } : (doctorRecord ? {
          name: doctorRecord.name,
          first_name: doctorRecord.name,
          last_name: "",
          specialization: (doctorRecord?.specializations && doctorRecord.specializations.length > 0)
            ? doctorRecord.specializations.join(", ")
            : doctorRecord.specialization,
          specializations: doctorRecord.specializations,
          registration_number: doctorRecord.registration_number || undefined,
          qualifications: doctorRecord.qualifications || undefined,
          department: doctorRecord.department || undefined,
          display_id: doctorRecord.display_id || undefined,
        } : null),
        clinic,
      });
      return true;
    } catch (err) {
      console.warn("offline PDF render failed", err);
      return false;
    }
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);

    // Fast path: device is known-offline, skip the server round-trip entirely.
    if (!navigator.onLine) {
      const ok = downloadOfflineCopy();
      if (!ok) showToast("Offline PDF unavailable — visit data not yet loaded", "error");
      setPdfLoading(false);
      return;
    }

    try {
      const result = await _getPdfResult();
      window.open(result.pdf_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      // Refused, not offline: the offline copy would hand over, from cache, the
      // prescription PDF the server just declined to issue.
      if (isPermissionDenied(e)) {
        showToast(actionErrorMessage(e, "Failed to generate PDF"), "error");
      } else if (isBackendUnreachable(e)) {
        const ok = downloadOfflineCopy();
        if (!ok) showToast("PDF generation requires a connection — go online to download", "error");
      } else {
        showToast(errMessage(e, "Failed to generate PDF"), "error");
      }
    } finally {
      setPdfLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSendToPharmacy = async () => {
    const confirmed = window.confirm(
      "This will copy a temporary link containing patient prescription details. " +
      "Only paste it into a trusted pharmacy channel. Continue?",
    );
    if (!confirmed) return;
    setPdfLoading(true);
    try {
      const result = await _getPdfResult(true);  // pharmacy_only: Diagnosis + Rx only
      if (!isTrustedPdfUrl(result.pdf_url)) {
        showToast("Refusing to share untrusted PDF URL", "error");
        return;
      }
      await navigator.clipboard.writeText(result.pdf_url);
      showToast("Prescription link copied — contains PHI; share carefully", "success");
    } catch (e) {
      showToast(errMessage(e, "Failed to generate PDF"), "error");
    } finally {
      setPdfLoading(false);
    }
  };

  // Finalise the prescription (Draft → Active) before sending. Generates the
  // S3 PDF — irreversible. Called only after the doctor confirms the prompt.
  const ensurePrescriptionFinalised = async () => {
    if (!latestRx) return;
    if (latestRx.status === "Active") return;
    try {
      const update = await PrescriptionDeliveryAPI.finalise(latestRx.prescription_id);
      setPrescriptions(prev =>
        prev.map(p => (p.prescription_id === update.prescription_id ? { ...p, ...update } : p)),
      );
    } catch (e) {
      if (e instanceof ApiError && e.isConflict) return; // already active on server
      throw e;
    }
  };

  // Sending requires the prescription to be Active (finalised + PDF in S3).
  // If it's still a Draft, we show a confirmation banner first — finalising
  // is irreversible (locks the prescription, uploads PDF to S3, creates an
  // audit record). The doctor must explicitly confirm before we proceed.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleEmailToPatient = () => {
    if (!latestRx) return;
    if (latestRx.status !== "Active") { setPendingDelivery("email"); return; }
    void _doSendEmail();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleWhatsappToPatient = () => {
    if (!latestRx) return;
    if (latestRx.status !== "Active") { setPendingDelivery("whatsapp"); return; }
    void _doSendWhatsapp();
  };

  const handleConfirmFinalise = async () => {
    const channel = pendingDelivery;
    setPendingDelivery(null);
    if (!channel) return;
    try {
      await ensurePrescriptionFinalised();
    } catch (e) {
      showToast(errMessage(e, "Failed to finalise prescription"), "error");
      return;
    }
    if (channel === "email") void _doSendEmail();
    else void _doSendWhatsapp();
  };

  const _doSendEmail = async () => {
    if (!latestRx) return;
    setEmailLoading(true);
    try {
      const record = await PrescriptionDeliveryAPI.sendEmail(latestRx.prescription_id);
      showToast(`Prescription emailed to ${record.recipient ?? "patient"}`, "success");
    } catch (e) {
      if (isInsufficientCreditsError(e)) {
        setShowBuyCredits(true);
      } else {
        showToast(errMessage(e, "Failed to email prescription"), "error");
      }
    } finally {
      setEmailLoading(false);
    }
  };

  const _doSendWhatsapp = async () => {
    if (!latestRx) return;
    setWaLoading(true);
    try {
      const record = await PrescriptionDeliveryAPI.sendWhatsapp(latestRx.prescription_id);
      showToast(`WhatsApp sent to ${record.recipient ?? "patient"}`, "success");
    } catch (e) {
      if (isInsufficientCreditsError(e)) {
        setShowBuyCredits(true);
      } else {
        showToast(errMessage(e, "Failed to send WhatsApp"), "error");
      }
    } finally {
      setWaLoading(false);
    }
  };

  const patientName = patient ? `${patient.first_name} ${patient.last_name}` : loading ? "Loading…" : "—";
  const patientAge  = patient?.date_of_birth ? calcAge(patient.date_of_birth) : "—";
  const visitDate   = visit?.visit_date
    ? new Date(visit.visit_date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  const finalizeDisabledReason = hasAiSuggested
    ? "Confirm or remove all AI Suggested medications first"
    : hasUnresolvedConflicts
      ? "Provide override reasons for all allergy conflicts first"
      : undefined;

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-[#f5f5f7]">
        <Sidebar />
        <div className="lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out min-h-screen">
          <AccessDenied label="this visit" detail={accessDenied} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <Sidebar />

      {/* Finalise confirmation — shown when doctor clicks Email/WhatsApp on a Draft prescription */}
      {pendingDelivery && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-base font-semibold text-slate-800">Finalise prescription?</p>
              <p className="text-sm text-slate-500">
                Sending via {pendingDelivery === "email" ? "email" : "WhatsApp"} requires finalising
                the prescription first. This locks it and uploads a PDF — it cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setPendingDelivery(null)}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmFinalise}
                className="flex-1 py-2.5 rounded-xl bg-teal-600 text-sm font-medium text-white hover:bg-teal-700 transition-colors"
              >
                Finalise &amp; Send
              </button>
            </div>
          </div>
        </div>
      )}

      {showBuyCredits && (
        <BuyCreditsModal onClose={() => setShowBuyCredits(false)} onPurchased={refetchWallet} />
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-3 px-4 py-3 rounded-2xl shadow-xl text-white text-sm font-medium max-w-sm w-full mx-4 ${toast.type === "error" ? "bg-red-600" : "bg-emerald-600"}`}>
          <span className="flex-1">{toast.msg}</span>
          <button onClick={() => setToast(null)} className="opacity-70 hover:opacity-100">✕</button>
        </div>
      )}

      <div className="lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out min-h-screen flex flex-col">
      <div className="min-h-0 flex flex-col overflow-hidden flex-1">

        {/* Page header */}
        <div className="px-6 pt-14 pb-5 lg:pt-5 border-b border-slate-100 flex-shrink-0">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-col gap-2">
              <button
                onClick={() => navigate(`/90sdiagnosis?visit_id=${visitId}&patient_id=${rawPatientId}&doctor_id=${doctorId}`)}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors w-fit"
              >
                <IcArrowLeft />
                Back to Diagnosis
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 leading-tight">Prescription Review</h1>
                <p className="text-sm text-slate-500 mt-1">Review and confirm medications before sending</p>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-7">
              <button
                onClick={handleDownloadPdf}
                disabled={pdfLoading}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {pdfLoading ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                ) : (
                  <IcDownload />
                )}
                {pdfLoading ? "Generating…" : "Download PDF"}
              </button>

              {/* Finalize button with tooltip when disabled */}
              <div className="relative group">
                <button
                  onClick={handleSubmit}
                  disabled={submitting || loading || !canFinalize}
                  className={`px-8 py-2.5 rounded-xl text-white text-sm font-semibold shadow-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed ${BLUE_GRADIENT}`}
                >
                  {submitting ? "Finalizing…" : "Finalize"}
                </button>
                {finalizeDisabledReason && (
                  <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-50">
                    <div className="bg-slate-800 text-white text-xs rounded-xl px-3 py-2 whitespace-nowrap shadow-lg">
                      {finalizeDisabledReason}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-col xl:flex-row gap-4 flex-1 min-h-0 overflow-y-auto xl:overflow-hidden px-5 py-4">

          {/* Prescription card */}
          <div className="w-full xl:flex-1 xl:overflow-y-auto min-w-0">
            {/* Life-Threatening Allergy Warning Banner */}
            {conflictingLifeThreateningCount > 0 && (
              <div
                data-testid="life-threatening-allergy-banner"
                role="alert"
                className="mb-4 rounded-xl bg-red-600 text-white px-3 py-2.5 flex items-center justify-between gap-3 text-xs font-semibold"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <IcWarning />
                  <span>
                    {conflictingLifeThreateningCount === 1
                      ? "1 Life-Threatening Allergy Conflict Detected"
                      : `${conflictingLifeThreateningCount} Life-Threatening Allergy Conflicts Detected`}
                  </span>
                </div>
                {/* Minimal badges for life-threatening allergens */}
                {conflictingLifeThreateningAllergies.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap flex-shrink-0">
                    {conflictingLifeThreateningAllergies.map(a => (
                      <span
                        key={a.allergy_id}
                        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white/20 text-white text-xs font-semibold"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-white" />
                        {a.allergen}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {scribeError && (
              <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <line x1="12" y1="8" x2="12" y2="13" />
                    <line x1="12" y1="16.5" x2="12" y2="17" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-amber-900">
                    AI Scribe couldn't process your recording — add medications manually
                  </p>
                  <p className="text-xs text-amber-800 mt-1">
                    Gemini returned 503. Your form is blank and ready for manual input. Recording has been discarded.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setScribeError(false);
                    const next = new URLSearchParams(searchParams);
                    next.delete("scribe_error");
                    setSearchParams(next, { replace: true });
                  }}
                  className="flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-900 transition-colors flex-shrink-0"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                  Dismiss
                </button>
              </div>
            )}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-8 py-8 flex flex-col gap-0">

              {/* Clinic branding */}
              <div className="flex flex-col items-center gap-3 pb-6">
                {clinicLogoUrl ? (
                  <img src={clinicLogoUrl} alt="Clinic logo" className="w-20 h-20 rounded-3xl object-cover shadow-md" />
                ) : (
                  <div className={`w-20 h-20 rounded-3xl flex items-center justify-center shadow-md ${BLUE_GRADIENT}`}>
                    <IcClinic />
                  </div>
                )}
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-slate-800">
                    {clinic?.name ?? (loading ? "Loading…" : "—")}
                  </h2>
                  {(clinic?.address || clinic?.location) && (
                    <p className="text-sm text-slate-400 mt-1">{clinic.address ?? clinic.location}</p>
                  )}
                  {clinic?.phone && (
                    <p className="text-xs text-slate-400">{clinic.phone}</p>
                  )}
                </div>
              </div>

              <hr className="border-slate-200 mb-6" />

              {/* Patient + Doctor info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div className="flex flex-col gap-1">
                  <p className="text-xs text-slate-400 font-medium mb-1">Patient Information</p>
                  <p className="text-lg font-bold text-slate-800">{patientName}</p>
                  <p className="text-sm text-slate-500">Age: {patientAge} • {patient?.gender ?? "—"}</p>
                  {patient?.contact_number && <p className="text-sm text-slate-500">{patient.contact_number}</p>}
                  <p className="text-sm text-slate-500 mt-2">Date: {visitDate}</p>

                  {/* Known Allergies */}
                  {allergies.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <IcShield /> Known Allergies ({allergies.length})
                      </p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        {allergies.map(a => {
                          const sevClass = ALLERGY_SEVERITY_BADGE[a.severity] || "text-slate-600 bg-slate-100 border-slate-200";
                          return (
                            <div key={a.allergy_id} className="inline-flex items-center gap-1.5">
                              <span className="text-sm font-semibold text-slate-800 leading-tight">{a.allergen}</span>
                              <span className={`text-[11px] font-semibold border rounded-full px-2 py-0.5 leading-none ${sevClass}`}>
                                {a.severity}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-xs text-slate-400 font-medium mb-1">Doctor Information</p>
                  <p className="text-lg font-bold text-slate-800">
                    {doctorDisplayName || (loading ? "Loading…" : "—")}
                  </p>
                  {/* Education / Qualifications */}
                  {doctorRecord?.qualifications && (
                    <p className="text-sm font-semibold text-slate-700">
                      {doctorRecord.qualifications}
                    </p>
                  )}
                  {/* Department */}
                  {(doctorRecord?.department || doctorRecord?.specialization || doctor?.specialization) && (
                    <p className="text-sm text-slate-500">
                      Department: {doctorRecord?.department || doctorRecord?.specialization || doctor?.specialization}
                    </p>
                  )}
                  {/* Doctor ID and Registration */}
                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                    {doctorRecord?.display_id && (
                      <span>
                        Doctor ID: <span className="font-mono font-semibold text-slate-700">{doctorRecord.display_id}</span>
                      </span>
                    )}
                    {(doctorRecord?.registration_number || doctor?.registration_number) && (
                      <span className="text-slate-400">
                        {doctorRecord?.display_id ? "• " : ""}Reg: {doctorRecord?.registration_number || doctor?.registration_number}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <hr className="border-slate-200 mb-6" />

              {/* Chief Complaint */}
              {visit?.chief_complaint && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <IcDocSection />
                    <h3 className="text-base font-bold text-slate-800">Chief Complaint</h3>
                  </div>
                  <div className="bg-sky-50 border border-sky-100 rounded-2xl px-4 py-3 text-sm text-slate-700">
                    {visit.chief_complaint}
                  </div>
                </div>
              )}

              {/* Diagnosis */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <IcDocSection />
                    <h3 className="text-base font-bold text-slate-800">Diagnosis</h3>
                  </div>
                  <VoiceBtn field="diagnosis" getValue={() => diagnosis} setValue={(v) => { setDiagnosis(v); if (diagnosisAiSuggested) setDiagnosisAiSuggested(false); }} />
                </div>
                <div
                  className={`relative rounded-2xl ${diagnosisAiSuggested ? "border-l-4 border-l-amber-400 border border-amber-200 bg-amber-50/40" : ""}`}
                >
                  <input
                    type="text"
                    value={diagnosis}
                    onChange={e => {
                      setDiagnosis(e.target.value);
                      if (diagnosisAiSuggested) setDiagnosisAiSuggested(false);
                    }}
                    placeholder="Enter diagnosis…"
                    className={`w-full h-11 px-4 ${diagnosisAiSuggested ? "pr-32 bg-transparent border-0" : "border border-slate-200"} rounded-2xl text-base sm:text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-300 transition-all`}
                  />
                  {diagnosisAiSuggested && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-200 text-[11px] font-semibold text-amber-700">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M12 2l2.39 5.46L20 8.27l-4.18 4.07L17 18.5 12 15.77 7 18.5l1.18-6.16L4 8.27l5.61-.81z" />
                      </svg>
                      AI Suggested
                    </span>
                  )}
                </div>
              </div>

              {/* Dental findings + plan (read-only; edited on the Diagnosis Pad) */}
              {visit?.dental_chart && (
                <div className="mb-6">
                  <DentalSummary
                    chart={visit.dental_chart}
                    onEdit={() => navigate(`/90sdiagnosis?visit_id=${visitId}&patient_id=${rawPatientId}&doctor_id=${doctorId}`)}
                  />
                </div>
              )}

              {/* Medications (Rx) */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <IcPillSection />
                    <h3 className="text-base font-bold text-slate-800">℞ Medications</h3>
                    {reviewMeds.length > 0 && (
                      <span className="w-5 h-5 rounded-full bg-sky-100 flex items-center justify-center text-xs font-bold text-sky-600">
                        {reviewMeds.length}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={handleOpenAddMed}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-sky-200 text-sky-600 text-xs font-semibold hover:bg-sky-50 transition-colors"
                  >
                    <IcPlus />
                    Add Medicine
                  </button>
                </div>

                {reviewMeds.length > 0 ? (
                  <div className="flex flex-col gap-3">
                    {reviewMeds.map((m, i) => (
                      <MedicationCard
                        key={m.medication_id}
                        med={m}
                        index={i}
                        onConfirm={handleConfirmMed}
                        onEdit={handleEditMed}
                        onRemove={handleRemoveMed}
                        onOverrideChange={handleOverrideChange}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="border border-dashed border-slate-200 rounded-2xl px-4 py-6 text-center">
                    <p className="text-sm text-slate-400">No medications yet</p>
                    <button
                      onClick={handleOpenAddMed}
                      className="mt-2 text-xs text-sky-500 hover:text-sky-700 font-medium transition-colors"
                    >
                      + Add a medicine
                    </button>
                  </div>
                )}

                {/* Finalize hint */}
                {hasAiSuggested && (
                  <p className="mt-3 text-xs text-amber-600 flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    Confirm or remove all AI Suggested medications to enable Finalize
                  </p>
                )}
              </div>

              {/* Investigations */}
              <div className="mb-6">
                <h3 className="text-base font-bold text-slate-800 mb-3">Investigations</h3>
                <InvestigationsTags tags={investigations} onChange={setInvestigations} />
              </div>

              {/* Advice & Instructions */}
              <div className="mb-6">
                <h3 className="text-base font-bold text-slate-800 mb-3">Advice &amp; Instructions</h3>
                <textarea
                  value={advice}
                  onChange={e => setAdvice(e.target.value)}
                  placeholder="Enter advice and instructions for the patient…"
                  rows={3}
                  className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-base sm:text-sm text-slate-700 placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300 transition-all"
                />
              </div>

              {/* Observation */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-bold text-slate-800">Observation</h3>
                  {obsStatus === "saving" && <span className="text-xs text-slate-400">Saving…</span>}
                  {obsStatus === "saved"  && <span className="text-xs text-emerald-500">Saved</span>}
                </div>
                <textarea
                  value={observation}
                  onChange={e => { obsIsDirtyRef.current = true; setObservation(e.target.value); }}
                  placeholder="Enter clinical observations, physical exam findings, or any additional notes…"
                  rows={4}
                  className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-base sm:text-sm text-slate-700 placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300 transition-all"
                />
              </div>

              {/* Follow-up */}
              <div className="mb-6">
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col gap-3 max-w-xs">
                  <div className="flex items-center gap-2">
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" className="text-slate-500">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <path d="M16 2v4M8 2v4M3 10h18" />
                    </svg>
                    <h3 className="text-[15px] font-bold text-slate-800">Follow-Up</h3>
                  </div>
                  <DateInput
                    value={followUp}
                    onChange={(e) => {
                      const picked = e.target.value;
                      const today = new Date().toISOString().split("T")[0];
                      if (picked && picked < today) return;
                      setFollowUp(picked);
                    }}
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full bg-slate-100 rounded-xl px-3 py-2.5 text-base sm:text-sm text-slate-700 outline-none"
                  />
                </div>
              </div>

              <hr className="border-slate-200 mb-5" />


            </div>
          </div>

          {/* Right sidebar */}
          <div className="w-full xl:w-80 xl:flex-shrink-0 flex flex-col gap-3 xl:overflow-y-auto">

            {/* Send Prescription — commented out; re-enable when pharmacy send is ready
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col gap-2">
              <h3 className="text-[15px] font-bold text-slate-800 mb-1">Send Prescription</h3>
              {latestRx && latestRx.status !== "Active" && (
                <p className="text-xs text-amber-600 -mt-1 mb-1">
                  Press <span className="font-semibold">Finalize</span> first to send to the patient.
                </p>
              )}
              <button
                disabled
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-white opacity-50 cursor-not-allowed ${BLUE_GRADIENT}`}
              >
                <IcSend />
                <div className="text-left">
                  <p className="text-sm font-semibold leading-tight">Send to Pharmacy</p>
                  <p className="text-xs opacity-80 mt-0.5">Available after visit is finalised</p>
                </div>
              </button>
              <button
                onClick={handleEmailToPatient}
                disabled={emailLoading || !latestRx || latestRx?.status !== "Active" || creditsGateState === "zero"}
                title={creditsGateState === "zero" ? "Out of notification credits — buy more to send" : undefined}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <IcMail />
                <div className="text-left">
                  <p className="text-sm font-semibold text-slate-800 leading-tight">{emailLoading? "Sending email...":"Email to Patient"}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {creditsGateState === "zero" ? "Out of notification credits" : "Send via patient email"}
                  </p>
                </div>
              </button>
              <button
                onClick={handleWhatsappToPatient}
                disabled={waLoading || !latestRx || latestRx?.status !== "Active" || creditsGateState === "zero"}
                title={creditsGateState === "zero" ? "Out of notification credits — buy more to send" : undefined}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <IcWhatsapp />
                <div className="text-left">
                  <p className="text-sm font-semibold text-slate-800 leading-tight">{waLoading ? "Sending WhatsApp...": "WhatsApp to Patient"}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {creditsGateState === "zero" ? "Out of notification credits" : "Send download link via WhatsApp"}
                  </p>
                </div>
              </button>
              {creditsGateState === "zero" && (
                <button
                  type="button"
                  onClick={() => setShowBuyCredits(true)}
                  className="w-full text-center text-xs font-semibold text-blue-600 hover:text-blue-700 py-1"
                >
                  Buy notification credits →
                </button>
              )}
            </div>
            */}

            {/* Summary */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col gap-3">
              <h3 className="text-[15px] font-bold text-slate-800">Summary</h3>
              <div className="flex flex-col gap-0">
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <span className="text-sm text-slate-500 flex items-center gap-1.5">
                    {conflictingLifeThreateningCount > 0 && (
                      <span
                        data-testid="medication-allergy-conflict-icon"
                        className="text-red-600 inline-flex items-center flex-shrink-0"
                        title="Life-threatening allergy conflict detected"
                      >
                        <IcWarning />
                      </span>
                    )}
                    Medications
                  </span>
                  <span className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center text-xs font-bold text-sky-600">{reviewMeds.length}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <span className="text-sm text-slate-500">AI Suggested</span>
                  <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-600">{reviewMeds.filter(m => m.ai_suggested).length}</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-sm text-slate-500">Lab Tests</span>
                  <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">{investigations.length}</span>
                </div>
              </div>
              {canFinalize ? (
                <div className="flex items-center gap-2">
                  <IcCheckCircle />
                  <span className="text-sm font-semibold text-emerald-600">Ready to finalize</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span className="text-sm font-semibold text-amber-600">Review pending</span>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col gap-2">
              <h3 className="text-[15px] font-bold text-slate-800 mb-1">Quick Actions</h3>
              <button
                onClick={() => showToast("Follow-up scheduling coming soon", "success")}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
              >
                <IcCalendarSmall />
                <span className="text-sm font-semibold text-slate-800">Schedule Follow-up</span>
              </button>
              <button
                onClick={() => showToast("Added to patient records", "success")}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
              >
                <IcDocSection />
                <span className="text-sm font-semibold text-slate-800">Add to Records</span>
              </button>
            </div>

          </div>
        </div>

      </div>
      </div>

      {/* Add / Edit Medicine modal */}
      <AddMedicineModal
        isOpen={addMedOpen}
        onClose={() => { setAddMedOpen(false); setEditingMed(null); }}
        onSave={handleSaveMed}
        initialValues={editingMed ?? undefined}
      />
    </div>
  );
};

export default ContinueDiagnosis;
